import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  IconButton,
  Dialog,
  useMediaQuery,
} from "@mui/material";
import { useCreateSumaryReportPolicyTermsMutation } from "../../../Api/attoDeskApi";
import {
  IPolicy,
  policyTermsArray,
} from "../../../Api/Interface/api.interface";
import { useFormik } from "formik";
import { useStyles } from "../../../theme/appStyles";
import CloseIcon from "@mui/icons-material/Close";
import { useNotifier } from "../../../Core/Notifier";
import AddIcon from "@mui/icons-material/Add";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Switch, { SwitchProps } from "@mui/material/Switch";
import { FaTrash } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface LoopingTableProps {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IPolicy;
}

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 50,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary,
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "4px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 24,
    height: 24,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#757575" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 12,
      height: 12,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 8,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 8,
    },
  },
}));

const LoopingTable: React.FC<LoopingTableProps> = ({
  openModel = false,
  handleCloseDialog,
  data,
}) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showErrorMessage, showMessage } = useNotifier();
  const [policyTermsData, { isLoading: loopingDataLoading }] =
    useCreateSumaryReportPolicyTermsMutation();

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const [currentTerm, setCurrentTerm] = useState<policyTermsArray[]>(
    data?.policySummaryReportArray || []
  );

  const formik = useFormik({
    initialValues: {
      id: data?.id,
      policySummaryReportArray: [],
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          id: values.id,
          policySummaryReportArray: currentTerm,
        };

        const addCompanyResponse = await policyTermsData(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleAddRow = () => {
    setCurrentTerm((prev) => [
      ...prev,
      {
        id: null,
        policyTermsDescription: "",
        isPolicyTermsSummaryReport: true,
        isPolicyTermsAll: true,
        isPolicyTermsPrintPdf: true,
        termsOrderNumber: null,
      },
    ]);
  };

  // const handleFieldChange = <K extends keyof policyTermsArray>(
  //   index: number,
  //   field: K,
  //   value: policyTermsArray[K]
  // ) => {
  //   const updatedRows = currentTerm.map((term, i) => {
  //     if (i === index) {
  //       return {
  //         ...term,
  //         [field]: value !== undefined ? value : term[field],
  //       };
  //     }
  //     return term;
  //   });

  //   setCurrentTerm(updatedRows);
  // };

  const handleFieldChange = <K extends keyof policyTermsArray>(
    index: number,
    field: K,
    value: policyTermsArray[K]
  ) => {
    const updatedRows = currentTerm.map((term, i) => {
      if (i === index) {
        const updatedTerm = {
          ...term,
          [field]: value !== undefined ? value : term[field],
        };

        // If "All" is being turned on, set other switches to true
        if (field === "isPolicyTermsAll" && value === true) {
          updatedTerm.isPolicyTermsSummaryReport = true;
          updatedTerm.isPolicyTermsPrintPdf = true;
        }

        return updatedTerm;
      }
      return term;
    });

    setCurrentTerm(updatedRows);
  };

  const handleDeleteRow = (index: number) => {
    const updatedRows = currentTerm.filter((_, i) => i !== index);
    setCurrentTerm(updatedRows);
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-describedby="alert-dialog-description"
        classes={{ paper: "inherit" }}
        fullScreen={isMobile}
        fullWidth={isMobile}
        maxWidth="lg"
      >
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.card}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  pt={2}
                  alignContent={"center"}
                >
                  <Typography className={classes.cardTitle}>
                    Summary Report (Additional Comments)
                  </Typography>
                </Grid>
              </Grid>
              {currentTerm.map((product, index) => (
                <Grid container spacing={2} p={"24px"} key={index}>
                  <Grid item lg={9} md={9} sm={9} xs={9}>
                    <ReactQuill
                      value={product.policyTermsDescription}
                      onChange={(newValue) =>
                        handleFieldChange(
                          index,
                          "policyTermsDescription",
                          newValue
                        )
                      }
                      placeholder="Enter Terms..."
                      theme="snow"
                      style={{ height: "150px" }} 
                      modules={{
                        toolbar: [
                          [{ font: [] }],
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          ["bold", "italic", "underline", "strike"],
                          [{ color: [] }, { background: [] }],
                          [{ script: "sub" }, { script: "super" }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ indent: "-1" }, { indent: "+1" }],
                          [{ direction: "rtl" }],
                          [{ align: [] }],
                          ["blockquote", "code-block"],
                          ["link", "image", "video"],
                          ["clean"],
                        ],
                      }}
                    />
                    {/* <TextField
                      placeholder="Enter Terms"
                      size="small"
                      value={product.policyTermsDescription}
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          "policyTermsDescription",
                          String(e.target.value)
                        )
                      }
                      multiline
                      rows={8}
                      className={classes.textField}
                      InputProps={{
                        sx: {
                          fontSize: 14,
                        },
                      }}
                      InputLabelProps={{
                        sx: {
                          fontSize: 14,
                        },
                      }}
                    /> */}
                  </Grid>
                  <Grid item lg={3} md={3} sm={3} xs={3}>
                    <Grid container spacing={2}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container>
                          <Grid item lg={3} md={3} sm={6} xs={6}>
                            <IOSSwitch
                              color="primary"
                              sx={{ mr: 2 }}
                              checked={product.isPolicyTermsSummaryReport}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "isPolicyTermsSummaryReport",
                                  Boolean(e.target.checked)
                                )
                              }
                            />
                          </Grid>
                          <Grid item lg={9} md={9} sm={6} xs={6}>
                            <Typography variant="subtitle1">
                              Summary Report
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container>
                          <Grid item lg={3} md={3} sm={6} xs={6}>
                            <IOSSwitch
                              color="primary"
                              sx={{ mr: 2 }}
                              checked={product.isPolicyTermsAll}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "isPolicyTermsAll",
                                  Boolean(e.target.checked)
                                )
                              }
                            />
                          </Grid>
                          <Grid item lg={9} md={9} sm={6} xs={6}>
                            <Typography variant="subtitle1">All</Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container>
                          <Grid item lg={3} md={3} sm={6} xs={6}>
                            <IOSSwitch
                              color="primary"
                              sx={{ mr: 2 }}
                              checked={product.isPolicyTermsPrintPdf}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "isPolicyTermsPrintPdf",
                                  Boolean(e.target.checked)
                                )
                              }
                            />
                          </Grid>
                          <Grid item lg={9} md={9} sm={6} xs={6}>
                            <Typography variant="subtitle1">
                              Print-PDF
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteRow(index)}
                        >
                          <FaTrash />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
              <Grid container>
                <Grid
                  item
                  lg={10}
                  md={10}
                  sm={12}
                  xs={12}
                  py={2}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="contained"
                    className={classes.addButton}
                    onClick={handleAddRow}
                  >
                    Add New
                  </Button>
                  {/* <Button
                    variant="contained"
                    className={classes.editButton}
                    onClick={handleAddRow}
                  >
                    <AddIcon />
                  </Button> */}
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
            <Card className={classes.card}>
              <Grid container spacing={3} p={"24px"}>
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    className={classes.addButton}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Box m={0.5}></Box>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlineIcon />}
                    className={classes.addButton}
                    onClick={() => formik.handleSubmit()}
                  >
                    Update
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Dialog>
    </div>
  );
};

export default LoopingTable;
